import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, ArrowCounterClockwise } from "@phosphor-icons/react";
import api, { apiErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { weeklyGross } from "./SalaryTable";

const todayET = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const blank = () => ({ employee: "", emp_type: "", check_amount: 0, cash_amount: 0, tax_wh_amount: 0, add_pay_amount: 0, effective_date: todayET(), notes: "", group: "" });
const MONEY = [["check_amount", "Weekly Check"], ["cash_amount", "Weekly Cash"], ["tax_wh_amount", "Tax WH (weekly)"], ["add_pay_amount", "Addit. Pmt/Week"]];

// mode "add" = new employee; mode "reactivate" = bring an archived employee back with new effective-dated pay.
export function AddEmployeeDialog({ open, mode = "add", employee, archivedOn, suggestions = [], unlockToken, onClose, onDone }) {
  const [f, setF] = useState(blank());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ ...blank(), employee: employee || "", effective_date: mode === "reactivate" && archivedOn > todayET() ? archivedOn : todayET() }); }, [open, employee, mode, archivedOn]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: MONEY.some(([m]) => m === k) ? Math.max(0, Number(v) || 0) : v }));
  const re = mode === "reactivate";
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(re ? "/admin/salaries/restore" : "/admin/salaries/employees", { ...f, unlock_token: unlockToken });
      toast.success(re ? `${f.employee} reactivated — pay from ${f.effective_date}` : `${data.employee} added${data.linked_timesheet ? " (linked to existing timesheet name)" : ""}`);
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, re ? "Reactivation failed" : "Could not add employee")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="salary-add-employee-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{re ? <ArrowCounterClockwise size={18} /> : <UserPlus size={18} />} {re ? `Reactivate ${employee}` : "Add employee"}</DialogTitle>
          <DialogDescription>
            {re ? `Pay stays $0 from the archive date (${archivedOn}). Enter the new weekly pay and the date it starts — earlier history is kept.`
              : "Weekly amounts, effective from the start date. Pick an existing timesheet name to link this salary to that person's timesheets."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Employee name</Label>
            <Input value={f.employee} onChange={(e) => set("employee", e.target.value)} disabled={re} list="salary-emp-suggestions" maxLength={80}
              placeholder="As written on the timesheet, e.g. Carlos A" className="h-9 mt-1" data-testid="salary-add-name" />
            <datalist id="salary-emp-suggestions">{suggestions.map((s) => <option key={s} value={s} />)}</datalist>
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <Input value={f.emp_type} onChange={(e) => set("emp_type", e.target.value)} maxLength={40} placeholder="e.g. 1099 or W2" className="h-9 mt-1" data-testid="salary-add-type" />
          </div>
          <div>
            <Label className="text-xs">{re ? "Reactivation date" : "Effective start date"}</Label>
            <Input type="date" value={f.effective_date} min={re ? archivedOn : undefined} onChange={(e) => set("effective_date", e.target.value)} className="h-9 mt-1" data-testid="salary-add-date" />
          </div>
          {MONEY.map(([k, l]) => (
            <div key={k}>
              <Label className="text-xs">{l}</Label>
              <div className="flex items-center gap-1 mt-1"><span className="text-slate-400">$</span>
                <Input type="number" step="0.01" min="0" value={f[k]} onChange={(e) => set(k, e.target.value)} className="h-9" data-testid={`salary-add-${k}`} /></div>
            </div>
          ))}
          <div className="col-span-2">
            <Label className="text-xs">Section</Label>
            <div className="flex gap-1.5 mt-1">
              {[["", "Employees"], ["painters", "Painters"]].map(([v, l]) => (
                <button key={v || "main"} type="button" onClick={() => set("group", v)} data-testid={`salary-add-group-${v || "main"}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${f.group === v ? "bg-[#327de3] border-[#327de3] text-white" : "bg-white border-slate-200 text-slate-600"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Notes</Label>
            <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} maxLength={5000} rows={2} className="mt-1" data-testid="salary-add-notes" />
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm flex justify-between">
          <span className="text-xs text-slate-600">Weekly Total Gross</span><span className="salary-gross font-extrabold tabular-nums" data-testid="salary-add-gross">${weeklyGross(f).toFixed(2)}</span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="salary-add-cancel">Cancel</Button>
          <Button onClick={go} disabled={busy || !f.employee.trim() || !f.effective_date} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="salary-add-confirm">
            {busy ? "Saving…" : re ? "Reactivate" : "Add employee"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
